<script lang="ts">
  import type { StoresKey } from "~/stores";
  import { getStoresContext } from "~/stores";
  import type { ManageAPI } from "~/manage-api/types";

  export let request_store_key: StoresKey;
  export let currentFobId: number | undefined = undefined;
  export let config = {
    restaurantFobIds: "",
    childrenFobIds: "",
    restaurantIcon: { desktop: "", mobile: "" },
    storeIcon: { desktop: "", mobile: "" },
    childrenIcon: { desktop: "", mobile: "" }
  };

  const { store, scdata } = getStoresContext(request_store_key);

  type FobIconType = 
    | "restaurant" 
    | "store" 
    | "children";

  // FOB icon mapping
  const fobIcons: Record<FobIconType, { desktop: string, mobile: string }> = {
    "restaurant": config.restaurantIcon,
    "store": config.storeIcon,
    "children": config.childrenIcon
  };

  // Parse the comma-separated ID strings into arrays of numbers
  function parseIds(idsString: string): number[] {
    if (!idsString) return [];
    return idsString.split(',')
      .map(id => parseInt(id.trim(), 10))
      .filter(id => !isNaN(id));
  }

  // Parse all FOB ID configurations
  const restaurantIds = parseIds(config.restaurantFobIds);
  const childrenIds = parseIds(config.childrenFobIds);

  // Function to determine icon type based on FOB ID
  function getFobIconType(fobId: number): FobIconType {
    if (restaurantIds.includes(fobId)) return "restaurant";
    if (childrenIds.includes(fobId)) return "children";
    // Default to store icon if no match is found
    return "store";
  }

  // Get FOB data from scdata
  $: fobData = ($scdata?.fieldsOfBusiness || []) as ManageAPI.Fob[];
  
  // Create a map of FOB IDs to FOB descriptions
  $: fobMap = fobData.reduce((acc: Record<number, string>, fob) => {
    if (fob && fob.id) {
      acc[fob.id] = fob.description || `FOB ${fob.id}`;
    }
    return acc;
  }, {});

  // Get the current FOB ID from props or from the store context
  $: currentFobIdToUse = currentFobId || $store?.fieldOfBusinessId;
  
  // Determine icon type based on FOB ID
  $: fobIconType = currentFobIdToUse 
    ? getFobIconType(currentFobIdToUse)
    : "store"; // Default to store if no FOB ID is available
  
  // Get the icon path
  $: iconPaths = fobIcons[fobIconType];
  
  // Get the FOB description for alt text
  $: fobDescription = currentFobIdToUse && fobMap[currentFobIdToUse] 
    ? fobMap[currentFobIdToUse] 
    : fobIconType;
</script>

<div class="fob-icon">
  {#if iconPaths && (iconPaths.desktop || iconPaths.mobile)}
    <picture>
      {#if iconPaths.desktop}
        <source media="(min-width: 768px)" srcset={iconPaths.desktop} />
      {/if}
      {#if iconPaths.mobile}
        <img src={iconPaths.mobile} alt="{fobDescription} icon" />
      {:else if iconPaths.desktop}
        <img src={iconPaths.desktop} alt="{fobDescription} icon" />
      {/if}
    </picture>
  {:else}
    <div class="fob-icon-fallback">#</div>
  {/if}
</div>

<style lang="scss">
  .fob-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 300px;
    height: 300px;

    img {
      object-fit: contain;
      max-width: 100%;
      max-height: 100%;
    }
  }

  .fob-icon-fallback {
    font-size: 10rem;
    font-weight: bold;
    color: #ccc;
    line-height: 1;

    @media (max-width: 768px) {
      font-size: 5rem;
    }
  }
</style>
